

import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { translations, type Language } from "@/utils/translations";
import CommonQuestions from "@/components/CommonQuestions";
import { getChatCompletion } from "@/lib/chatCompletion";
import { initializeOpenAIClient } from "@/lib/openai";
import { useQuery } from "@tanstack/react-query";
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useUserProfile } from "@/hooks/useUserProfile";
import { useChatLimits } from "@/hooks/useChatLimits";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";
import ChatHeader from "@/components/chat/ChatHeader";
import ChatContent from "@/components/chat/ChatContent";
import ChatInput from "@/components/chat/ChatInput";
import ChatActions from "@/components/chat/ChatActions";
import ModernChatInterface from "@/components/chat/ModernChatInterface";
import { ChatMessage } from "@/types/chat";

// Declare gtag for TypeScript
declare global {
  function gtag(...args: any[]): void;
}

const availableLanguages = Object.keys(translations) as Language[];

// Helper function to extract text content from ChatMessage content
const getMessageContent = (content: ChatMessage['content']): string => {
  if (typeof content === 'string') {
    return content;
  }
  if (Array.isArray(content)) {
    return content
      .map(part => {
        if ('text' in part) {
          return part.text;
        }
        return '';
      })
      .join(' ');
  }
  return '';
};

const Index = () => {
  const [input, setInput] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [currentResponse, setCurrentResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [language, setLanguage] = useState<Language>("es");
  const [isSaving, setIsSaving] = useState(false);
  const [openaiClient, setOpenaiClient] = useState<any>(null);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const { toast } = useToast();
  const t = translations[language];
  
  const { userPlan, session } = useUserProfile();
  const { 
    hasReachedLimit, 
    remainingRequests, 
    incrementRequestCount, 
    decrementRequestCount 
  } = useChatLimits(userPlan);

  // Text-to-speech functionality
  const { 
    isSpeaking, 
    isMuted: ttsIsMuted, 
    speakText, 
    toggleMute: toggleTTSMute, 
    cleanup: cleanupTTS 
  } = useTextToSpeech();

  const handleFinalTranscript = async (finalTranscript: string) => {
    if (finalTranscript.trim() && !hasReachedLimit) {
      setInput(finalTranscript.trim());
      await handleSubmitWithText(finalTranscript.trim());
    }
  };

  const { isListening, startListening, stopListening } = useSpeechRecognition({
    onFinalTranscript: handleFinalTranscript,
    onTranscriptChange: (newTranscript: string) => {
      if (isListening && !hasReachedLimit) {
        setInput(newTranscript);
      }
    },
    isDisabled: isLoading || hasReachedLimit
  });

  useEffect(() => {
    const initOpenAI = async () => {
      try {
        const client = await initializeOpenAIClient();
        setOpenaiClient(client);
      } catch (error) {
        console.error('Failed to initialize OpenAI client:', error);
      }
    };

    initOpenAI();
  }, []);

  // Clear conversation context on page load to ensure fresh start
  useEffect(() => {
    const clearContextOnLoad = async () => {
      if (session?.user?.id) {
        try {
          const { clearConversationContext } = await import('@/lib/conversationContext');
          await clearConversationContext(session.user.id);
          console.log('Conversation context cleared for fresh start');
        } catch (error) {
          console.error('Error clearing conversation context on load:', error);
        }
      }
    };

    clearContextOnLoad();
  }, [session?.user?.id]);

  const { data: savedChats, refetch: refetchSavedChats } = useQuery({
    queryKey: ["saved-chats"],
    queryFn: async () => {
      if (!session?.user?.id) return [];

      const { data, error } = await supabase
        .from("saved_chats")
        .select("*")
        .eq("user_id", session?.user?.id)
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          variant: "destructive",
          title: t.error,
          description: t.failed_load_chats,
        });
        return [];
      }

      return data || [];
    },
    enabled: !!session?.user?.id,
  });

  useEffect(() => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'conversion', {'send_to': 'AW-17096274101/My1fCM-EjM4aELXhkdg_'});
    }
  }, []);

  const handleSubmitWithText = async (text: string) => {
    if (!text.trim() || !openaiClient || hasReachedLimit) return;

    setCurrentQuestion(text.trim());
    setCurrentResponse("");
    setInput("");
    setIsLoading(true);

    incrementRequestCount();

    try {
      let response;
      
      // Use context-aware chat completion if user is logged in
      if (session?.user?.id) {
        const { getChatCompletionWithContext } = await import('@/lib/chatCompletionWithContext');
        response = await getChatCompletionWithContext(openaiClient, text, session.user.id);
      } else {
        // Fallback to regular chat completion for anonymous users with current conversation history
        response = await getChatCompletion(openaiClient, text, conversationHistory);
      }

      if (response) {
        const responseContent = typeof response === 'string' ? response : getMessageContent(response);
        setCurrentResponse(responseContent);

        // Update conversation history in state
        const newUserMessage: ChatMessage = { role: "user", content: text };
        const newAssistantMessage: ChatMessage = { role: "assistant", content: responseContent };
        setConversationHistory(prev => [...prev, newUserMessage, newAssistantMessage]);

        if (session?.user?.id) {
          await saveChat(text, responseContent);
        }

        // Auto-play text-to-speech after 0.5 seconds delay
        if (responseContent && !ttsIsMuted) {
          await speakText(responseContent, 500);
        }
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t.error,
        description: t.failed_send_message,
      });
      decrementRequestCount();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmitWithText(input);
  };

  const saveChat = async (question: string, answer: string) => {
    if (!session?.user?.id) return;

    try {
      const title = question.substring(0, 50);
      const content = JSON.stringify({ question, answer });

      const { error } = await supabase.from("saved_chats").insert({
        user_id: session.user.id,
        title: title,
        content: content,
      });

      if (error) {
        console.error('Error saving chat:', error);
      }
    } catch (error) {
      console.error('Error in saveChat:', error);
    }
  };

  const handleSaveChat = async () => {
    if (!currentQuestion || !currentResponse) {
      toast({
        variant: "destructive",
        title: t.error,
        description: t.no_messages_save,
      });
      return;
    }

    if (!session?.user?.id) {
      toast({
        variant: "destructive",
        title: "Inicia sesión",
        description: "Debes iniciar sesión para guardar chats",
      });
      return;
    }

    setIsSaving(true);

    try {
      const title = currentQuestion.substring(0, 50);
      const content = JSON.stringify({
        question: currentQuestion,
        answer: currentResponse,
      });

      const { error } = await supabase.from("saved_chats").insert({
        user_id: session?.user?.id,
        title: title,
        content: content,
      });

      if (error) {
        throw error;
      }

      toast({
        title: t.success,
        description: t.chat_saved,
      });

      await refetchSavedChats();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t.error,
        description: t.failed_save_chat,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
  };

  // Function to clear conversation history
  const handleClearConversation = async () => {
    setConversationHistory([]);
    setCurrentQuestion("");
    setCurrentResponse("");
    
    // Stop any current speech
    cleanupTTS();
    
    if (session?.user?.id) {
      try {
        const { clearConversationContext } = await import('@/lib/conversationContext');
        await clearConversationContext(session.user.id);
      } catch (error) {
        console.error('Error clearing conversation context:', error);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupTTS();
    };
  }, [cleanupTTS]);

  return (
    <ModernChatInterface
      input={input}
      setInput={setInput}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isListening={isListening}
      hasReachedLimit={hasReachedLimit}
      openaiClient={openaiClient}
      currentQuestion={currentQuestion}
      currentResponse={currentResponse}
      startListening={startListening}
      stopListening={stopListening}
      onSaveChat={handleSaveChat}
      isSaving={isSaving}
      language={language}
      onLanguageChange={handleLanguageChange}
      availableLanguages={availableLanguages}
      userPlan={userPlan}
      remainingRequests={remainingRequests}
      conversationHistory={conversationHistory}
      onClearConversation={handleClearConversation}
      isSpeaking={isSpeaking}
      isMuted={ttsIsMuted}
      onToggleMute={toggleTTSMute}
    />
  );
};

export default Index;
